/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { Button, Col, Row } from 'reactstrap';
import axios from 'axios';
import { get, some } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactLoading from 'react-loading';

import Step3Table from './Step3Table';
import EditLotModal from './EditLotModal';
import {
  validateEst,
  validateLot,
  validateLotTitle,
  validateLotDescription,
  validateShippingField,
  validateStartingBid,
  validateInvNumber,
  validateReserve
} from '../diy-utils/EditLotValidation';

import getSpreadsheetError from '../diy-utils/getSpreadsheetError';
import { handleSelectAllById, handleRowCheckboxWithSet } from '../diy-utils/checkboxHandlers';
import ConfirmModal from '../../../utilities/form-components/ConfirmModal';
import InlineAlert from '../../../utilities/form-components/InlineAlert';

// After a user successfully uploads a spreadsheet, we arrive here
class Step3SuccessUpload extends Component {
  constructor(props) {
    super(props);

    // This only ever happens once so we're configuring an initial state for lots - which is ok.
    this.state = {
      currentPage: 0,
      deleteLotModal: false,
      deleteSelectedLotsModal: false,
      editLotModal: false,
      error: null,
      invNumberArray: [],
      itemsPerPage: 50,
      loading: false,
      lotData: null,
      lotNumberArray: [],
      lots: props.lots,
      orderBids: null,
      rowIndex: null,
      selectedLots: new Set(),
      showOnlyErrors: false,
      spreadsheetErrors: props.spreadsheetErrors,
      spreadsheetWarnings: props.spreadsheetWarnings,
      uploadAgainModal: false
    };
  }

  async componentDidMount() {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    try {
      const orderBidResponse = await axios.get(`/cps/app/catalog/${catalogID}/orderbids`);
      const orderBids = get(orderBidResponse, 'data.data.orderbids', null);

      this.setState({ orderBids });
    } catch (error) {
      this.setState({ error });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    /**
     * I don't want the entire component (and sub-components) to re-render after we fetch
     * the order bids data since this data is only passed into the edit modals
     */
    if (this.state.orderBids !== nextState.orderBids) {
      return false;
    }

    return true;
  }

  deleteLot = () => {
    const { id } = this.state;

    const lotsCopy = [...this.state.lots];
    const lotIndex = lotsCopy.findIndex(lot => lot.id === id);

    const updatedLotNumber = lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt;
    const updatedInvNumber = lotsCopy[lotIndex].invNumber;
    const updatedLot = {
      prevLotNumber: updatedLotNumber,
      prevInvNumber: updatedInvNumber,
      lotId: id,
    };
    lotsCopy.splice(lotIndex, 1);

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(lotsCopy, [
      updatedLot,
    ]);

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      lots: resolvedLots,
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });
  };

  /**
   * updateSpreadsheetErrors takes in the array of lots in the spreadsheet (after deletion),
   * and an array of all the deleted lot numbers.
   *
   * The errors are handled in two parts:
   *
   * 1. The spreadsheetErrors JSON object in state which contains grouped errors
   * by error code, and each error group contains all affected lots in an ES6 set.
   *
   * 2. The hasError attribute for the lot object in the table - this allows us to
   * call a filter for hasError = true in the table.  If the lot is being deleted - this
   * doesn't matter.  If the lot is being edited - it can no longer have errors so we
   * set hasError to false in the submitEditLot function.
   *
   * This function will parse the existing spreadsheet errors and remove all
   * references to every lot in the spreadsheetErrors object.  If there are no more lotNumbers
   * for an existing error - delete the entire error out of the spreadsheetErrors
   * object.
   *
   * Note that there are two special cases in this function for duplicate lot # and duplicate
   * inventory #.  These cases also have to find associated duplicates and
   * sometimes set hasError to false.
   *
   * This function is called whenever a user deletes a lot, deletes selected lots,
   * or edits a lot so that we can update the list of errors the user sees.
   *
   * The returned values are:
   * 1. The updated lot array (resolvedLots)
   * 2. The updated spreadsheetErrors JSON object (resolvedErrors)
   */

  /**
   *
   * @param {*} lots
   * @param {Array<{prevLotNumber: string, prevInvNumber: string, lotId: string}>} lotNumberArray
   * @returns
   */
  updateSpreadsheetErrors = (lots, lotNumberArray) => {
    const lotsCopy = [...lots];
    const spreadsheetErrorCopy = this.state.spreadsheetErrors;

    if (!spreadsheetErrorCopy) {
      return { resolvedLots: lots, resolvedErrors: null };
    }

    lotNumberArray.forEach(lot => {
      Object.keys(spreadsheetErrorCopy).forEach(errorCode => {
        const errorsForCode = spreadsheetErrorCopy[errorCode];
        const filteredLots = lots.filter(l => l.invNumber === lot.prevInvNumber
          && (l.lotNumber + l.lotExt) === lot.prevLotNumber && l.id !== lot.lotId);

        if (!(filteredLots.length > 1)) {
          if (lots.filter(l => (l.lotNumber + l.lotExt) === lot.prevLotNumber && l.id !== lot.lotId).length <= 0) {
            errorsForCode.lotNumbers.delete(lot.prevLotNumber);
          }

          if (lots.filter(l => l.invNumber === lot.prevInvNumber && l.id !== lot.lotId).length <= 0) {
            errorsForCode.invNumbers.delete(lot.prevInvNumber);
          }
        }

        errorsForCode.referenceMsg.delete(lot.lotId.toString());

        if ((errorsForCode.lotNumbers.size === 0 && errorsForCode.invNumbers.size === 0) || errorsForCode.referenceMsg.size === 0) {
          delete spreadsheetErrorCopy[errorCode];
        }
      });
    });

    return { resolvedLots: lotsCopy, resolvedErrors: spreadsheetErrorCopy };
  };

  deleteSelectedLots = () => {
    const lotsCopy = [...this.state.lots];
    const { selectedLots } = this.state;

    const deletedLot = [];
    const reverseArray = [...selectedLots].sort((a, b) => b - a);
    reverseArray.forEach(lotId => {
      const lotIndex = lotsCopy.findIndex(lot => lot.id === lotId);
      if (lotIndex >= 0) {
        const updatedLotNumber = lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt;
        const updatedInvNumber = lotsCopy[lotIndex].invNumber;
        const updatedLot = {
          prevLotNumber: updatedLotNumber,
          prevInvNumber: updatedInvNumber,
          lotId: lotIndex,
        };
        deletedLot.push(updatedLot);
        lotsCopy.splice(lotIndex, 1);
      }
    });

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(
      lotsCopy,
      deletedLot
    );

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      currentPage: 0,
      lots: resolvedLots,
      selectedLots: new Set(),
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });
  };

  editLot = id => {
    const { lots } = this.state;
    const lotData = lots.find(lot => lot.id === id);
    // Show edit lot modal - pass in the selected lot data
    this.handleModal('editLotModal', true, lotData, id);
  };

  // Toggle a table row as selected or unselected by the id
  handleCheckbox = rowIndex => {
    const { selectedLots } = this.state;
    this.setState({ selectedLots: handleRowCheckboxWithSet(rowIndex, selectedLots) });
  };

  // Select all or unselect all items currently on the page
  handleCheckboxAll = () => {
    const { currentPage, itemsPerPage, showOnlyErrors, selectedLots } = this.state;
    let lotsCopy = [...this.state.lots];
    if (showOnlyErrors) {
      lotsCopy = lotsCopy.filter(lot => lot.hasError);
    }

    this.setState({
      selectedLots: handleSelectAllById(currentPage, itemsPerPage, lotsCopy, selectedLots)
    });
  };

  handleErrorCheckboxFilter = () => {
    const { showOnlyErrors } = this.state;
    this.setState({
      currentPage: 0,
      selectedLots: new Set(),
      showOnlyErrors: !showOnlyErrors
    });
  };

  // Reusable modal handler
  handleModal = (modalName, showModal, lotData = null, id = null) => {
    /**
     * We need to do some initial validation for the user before the edit lot modal launches
     * so that we can load the modal in an errored state
     */
    if (modalName === 'editLotModal' && showModal) {
      const { lots, spreadsheetErrors } = this.state;
      const {
        lotNumber,
        lotExt,
        lotTitle,
        lotDescription,
        shipWidth,
        shipHeight,
        shipLength,
        shipWeight,
        loEst,
        hiEst,
        startingBid,
        invNumber,
        reserve
      } = lotData;

      const lotNumberArray = lots.map(lot => lot.lotNumber + lot.lotExt);
      const invNumberArray = lots.map(lot => lot.invNumber);

      /**
       * Remove the first occurrence of the maps that match so that
       * we can do duplicate checks in validation
       */
      const lotNumberIndex = lotNumberArray.findIndex(lotNum => lotNum === lotNumber + lotExt);
      const invNumberIndex = invNumberArray.findIndex(invNum => invNum === invNumber);
      lotNumberArray.splice(lotNumberIndex, 1);
      invNumberArray.splice(invNumberIndex, 1);

      const deletedLots = get(spreadsheetErrors[112], 'lotNumbers', new Set());

      // Lot Number and Lot Extension have to be validated together
      const { lotNumber: validatedLotNumber, lotExt: validatedLotExt } = validateLot(
        deletedLots,
        lotNumber,
        lotExt,
        lotNumberArray,
        invNumber,
        this.props.isGoldPlatinum,
      );

      // Low Estimate and High Estimate have to be validated together
      const { loEst: validatedLoEst, hiEst: validatedHiEst } = validateEst(loEst, hiEst);

      const editLotValidation = {
        lotNumber: validatedLotNumber,
        lotExt: validatedLotExt,
        lotTitle: validateLotTitle(lotTitle),
        lotDescription: validateLotDescription(lotDescription),
        shipWidth: validateShippingField('Shipping Width', shipWidth),
        shipHeight: validateShippingField('Shipping Height', shipHeight),
        shipLength: validateShippingField('Shipping Length', shipLength),
        shipWeight: validateShippingField('Total Weight', shipWeight),
        // Don't need shipping / shippingIntl since this is live/timed only
        loEst: validatedLoEst,
        hiEst: validatedHiEst,
        startingBid: validateStartingBid(startingBid),
        invNumber: validateInvNumber(invNumber, invNumberArray, lotNumber, this.props.isGoldPlatinum),
        reserve: validateReserve(reserve)
      };

      this.setState({
        [modalName]: showModal,
        lotData,
        editLotValidation,
        id,
        invNumberArray,
        lotNumberArray
      });
    } else {
      this.setState({
        [modalName]: showModal,
        lotData,
        id
      });
    }
  };

  // The user changed the pagination value for the table
  handlePageClick = data => {
    this.setState({ currentPage: data.selected });
  };

  // The user changed the number of lots per page - set the current page back to 0
  handlePageSize = e => {
    // For some reason, even though the value is a number, this is being changed to a string
    this.setState({ currentPage: 0, itemsPerPage: +e.target.value });
  };

  // The user edited their lot data inside the modal and there are no more errors!
  submitEditLot = lotData => {
    const lotsCopy = [...this.state.lots];
    const lotIndex = lotsCopy.findIndex(lot => lot.id === lotData.id);
    const updatedLotNumber = lotsCopy[lotIndex].lotNumber + lotsCopy[lotIndex].lotExt;
    const updatedInvNumber = lotsCopy[lotIndex].invNumber;

    const updatedLot = {
      prevLotNumber: updatedLotNumber,
      prevInvNumber: updatedInvNumber,
      newLotNumber: lotData.lotNumber,
      newInvNumber: lotData.invNumber,
      lotId: lotIndex,
    };

    lotsCopy[lotIndex] = lotData;
    lotsCopy[lotIndex].hasError = false;

    const { resolvedLots, resolvedErrors } = this.updateSpreadsheetErrors(lotsCopy, [updatedLot]);

    // If we deleted all of the errors - need to disable the error filter now
    let errorFilter = false;
    if (resolvedLots.some(lot => lot.hasError)) {
      errorFilter = this.state.showOnlyErrors;
    }

    this.setState({
      editLotModal: false,
      lots: resolvedLots,
      showOnlyErrors: errorFilter,
      spreadsheetErrors: resolvedErrors
    });
  };

  async submitLotData() {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    this.setState({ loading: true });

    // Delete the unique lot id field and hasError field that the FE had to add
    const lotsCopy = [...this.state.lots];
    lotsCopy.forEach(lot => {
      delete lot.id;
      delete lot.hasError;
    });

    try {
      const postUrl = `/cps/app/catalog/${catalogID}/lots/update`;
      await Promise.all([
        axios.post(
          postUrl,
          { lots: lotsCopy },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        ),
        axios.post(`/cps/app/diy-step-tracker/${catalogID}`, 4, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }),
      ]);
      // This call needs to wait for the lots to be submitted successfully
      // Only call shipping-review if Invaluable Shipping is enabled
      if (this.props.isInvaluableShippingEnabled) {
        try {
          await axios.post(
            `/cps/app/catalog/${catalogID}/shipping-review`,
            null,
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (err) {
          console.error('Error creating shipping review for lots => ', err);
        }
      }
    } catch (error) {
      this.setState({ error, loading: false });
    }
  }

  async saveAndExit() {
    await this.submitLotData();
    this.props.history.push('/my-auctions/current');
  }

  async submitStepThree() {
    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    await this.submitLotData();

    this.props.history.push(
      `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/4`
    );
  }

  toggleDeleteModal = () => {
    this.setState({ deleteLotModal: !this.state.deleteLotModal });
  };

  toggleDeleteSelectedLotsModal = () => {
    this.setState({ deleteSelectedLotsModal: !this.state.deleteSelectedLotsModal });
  };

  toggleUploadAgainModal = () => {
    this.setState({ uploadAgainModal: !this.state.uploadAgainModal });
  };

  render() {
    const { error, loading } = this.state;

    if (error) {
      return <InlineAlert />;
    }

    if (loading) {
      return (
        <div className="loading-spinner">
          <ReactLoading className="mx-auto mb-4" type="spin" color="black" />
          <p className="font-weight-bold">
            This may take a moment, please do not refresh the browser...
          </p>
        </div>
      );
    }

    const {
      match: {
        params: { type }
      },
      isGoldPlatinum,
    } = this.props;

    const {
      currentPage,
      deleteLotModal,
      deleteSelectedLotsModal,
      lotData,
      editLotValidation,
      editLotModal,
      invNumberArray,
      itemsPerPage,
      lotNumberArray,
      lots,
      orderBids,
      rowIndex,
      selectedLots,
      showOnlyErrors,
      spreadsheetErrors,
      spreadsheetWarnings,
      uploadAgainModal
    } = this.state;

    const originalNumLots = this.props.lots.length;
    const numLots = lots.length;

    const maxLotsExceeded = spreadsheetWarnings.some(warning => warning.errorCode === 113);
    const orderBidWarnings = spreadsheetWarnings.filter(warning => warning.errorCode === 116);

    let orderBidWarningBanner = null;
    if (orderBidWarnings.length > 0) {
      const warningLotArray = new Set();
      orderBidWarnings.forEach(warning => warningLotArray.add(warning.lotNumber));

      // BE sends either an array of lot numbers, or null
      const secondaryText = [...warningLotArray]
        .map(value => (value ? `Lot ${value}` : ''))
        .join(', ');

      orderBidWarningBanner = (
        <InlineAlert
          baseText={`Your starting bid ${
            type === 'timed' ? ' / reserve' : ''
          } cannot be updated after a bid has been placed on a lot.`}
          color="warning"
          secondaryText={secondaryText}
          showPhones={false}
          titleText={`Starting bid ${type === 'timed' ? ' / Reserve' : ''} not updated`}
        />
      );
    }

    // Pass this value into the edit lot modal
    let hasOrderBids = false;
    // rowIndex ranges from 0 - N so it can be equal to 0, so we can't just say (... && rowIndex)
    if (orderBids && rowIndex >= 0) {
      /**
       * Safeguard for when a user closes the modal since rowIndex will be set
       * back to null (this code still executes /shrug)
       */
      const lotNum = get(lots[rowIndex], 'lotNumber', -1);
      if (lotNum >= 0) {
        const lotExt = get(lots[rowIndex], 'lotExt', '');
        hasOrderBids = orderBids.some(item => lotNum + lotExt === item);
      }
    }

    const deletedLots = get(spreadsheetErrors[112], 'lotNumbers', new Set());

    const hasSpreadsheetErrors = some(spreadsheetErrors);

    return (
      <div className="mt-4">
        <Row>
          {/* Start Left Column */}
          <Col xs={8}>
            <strong>Check the Item List below for all required (*) item details.</strong>
            <p>To complete your upload, click &quot;Continue&quot;.</p>
            <p>
              Please note that starting bids are not required. Our system will automatically
              calculate starting bids based on the estimate amounts for each item. If your file does
              not include a starting bid or estimates, then our system will set the starting bid to
              your lowest bid increment.
            </p>
            <p>
              For example, if your bid increment from $1-$99 is $5, the starting bid will be set to
              $5.
            </p>
          </Col>

          {/* Start Right Column */}
          <Col xs={4}>
            <strong>Edit One Item (at a time)</strong>
            <p className="mb-0">
              Select the edit icon ( <FontAwesomeIcon icon="edit" /> ) to edit items in the list one
              at a time.
            </p>
            <p>Click &quot;Save Changes&quot; after each edit.</p>
            <strong>Edit Multiple Items</strong>
            <p>
              Edit your original item list file in your spreadsheet program. Resave in the required
              format (.csv, .xls, .xlsx). Click &quot;Go Back &amp; Upload Items Again&quot; to
              replace the collection list below.
            </p>
          </Col>
        </Row>

        {!hasSpreadsheetErrors && (
          <InlineAlert
            baseText="Your file upload was a success!"
            color="success"
            icon="check"
            showPhones={false}
          />
        )}

        {maxLotsExceeded && (
          <InlineAlert
            titleText={`You have reached your maximum of ${originalNumLots} lots for this sale.`}
            baseText={`Your first ${originalNumLots} lots have been uploaded.  Please create a new session to add additional items.`}
            color="warning"
            showPhones={false}
          />
        )}
        {hasSpreadsheetErrors
          && Object.keys(spreadsheetErrors).map(errorCode => {
            const objError = spreadsheetErrors[errorCode];
            const secondaryText = [];

            // Do some logic here to make the error more friendly
            const { title, description } = getSpreadsheetError(
              errorCode,
              objError.displayMsg,
              objError.lotNumbers
            );

            objError.referenceMsg.forEach((ref) => {
              const index = lots.findIndex(l => l.id.toString() === ref.toString());
              const lot = lots[index];

              if (lot) {
                if (lot.lotNumber + lot.lotExt !== '') {
                  secondaryText.push(`Lot ${lot.lotNumber + lot.lotExt}`);
                } else {
                  secondaryText.push(`Inv #${lot.invNumber}`);
                }
              }
            });

            return (
              <InlineAlert
                key={errorCode}
                baseText={description}
                secondaryText={secondaryText.join(', ')}
                showPhones={false}
                titleText={title}
              />
            );
          })}

        {orderBidWarningBanner}

        <p className="mb-0">
          <strong>Item Summary</strong> (Total # of Items: {numLots})
        </p>
        <p>* = Required</p>

        <Step3Table
          currentPage={currentPage}
          editLot={this.editLot}
          handleCheckbox={this.handleCheckbox}
          handleCheckboxAll={this.handleCheckboxAll}
          handleErrorCheckboxFilter={this.handleErrorCheckboxFilter}
          handleModal={this.handleModal}
          handlePageClick={this.handlePageClick}
          handlePageSize={this.handlePageSize}
          itemsPerPage={itemsPerPage}
          lots={lots}
          selectedLots={selectedLots}
          showOnlyErrors={showOnlyErrors}
          spreadsheetErrors={spreadsheetErrors}
          isGoldPlatinum={isGoldPlatinum}
        />

        <ConfirmModal
          actionFunction={this.props.goBackToUpload}
          bodyText="Are you sure you want to upload these items again? This will delete all the items you just uploaded."
          buttonText="Upload Again"
          showModal={uploadAgainModal}
          title="Upload Items Again"
          toggleFunction={this.toggleUploadAgainModal}
        />

        <ConfirmModal
          actionFunction={this.deleteLot}
          bodyText="Are you sure you want to delete this lot?"
          buttonText="Delete"
          showModal={deleteLotModal}
          title="Delete Lot"
          toggleFunction={this.toggleDeleteModal}
        />

        <ConfirmModal
          actionFunction={this.deleteSelectedLots}
          bodyText="Are you sure you want to delete all selected lots?"
          buttonText="Delete"
          showModal={deleteSelectedLotsModal}
          title="Delete Lots"
          toggleFunction={this.toggleDeleteSelectedLotsModal}
        />

        <EditLotModal
          deletedLots={deletedLots}
          editLotValidation={editLotValidation}
          handleModal={this.handleModal}
          hasOrderBids={hasOrderBids}
          invNumberArray={invNumberArray}
          lotData={lotData}
          lotNumberArray={lotNumberArray}
          modalName="editLotModal"
          showEditLotModal={editLotModal}
          submitEditLot={this.submitEditLot}
          type={type}
          isGoldPlatinum={isGoldPlatinum}
        />

        <div className="mt-4">
          <Button
            className={hasSpreadsheetErrors ? 'cursor-not-allowed' : ''}
            disabled={hasSpreadsheetErrors}
            color="primary"
            onClick={() => this.submitStepThree()}
          >
            Continue
          </Button>

          {!hasSpreadsheetErrors && (
            <Button className="ml-5" color="link" onClick={() => this.saveAndExit()}>
              Save &amp; Exit
            </Button>
          )}

          <Button
            className="ml-5"
            color="link"
            onClick={() => this.handleModal('uploadAgainModal', true)}
          >
            Go Back &amp; Upload Items Again
          </Button>
        </div>
      </div>
    );
  }
}

export default withRouter(Step3SuccessUpload);
